<script lang="ts" setup>
  import { marked } from 'marked'
  import { FARE_TABLE_CLUB_LEVEL } from '@/components/fare-table/types'
  import type { TFareTableClubLevel } from '@/components/fare-table/types'
  import type { AssetStoryblok, ServiceSummaryStoryblok } from '@/storyblok/component-types-sb'
  import useFareTableExpandableRows from '@/components/fare-table/useExpandableRows'
  import { fareHeaderColorVariants } from '@/components/fare-table/fareHeaderVariants'
  import { cva } from 'class-variance-authority'
  import FareTableServiceSummary from '@/storyblok/content/fare-table/ServiceSummary.vue'
  import FareTableServiceBody from '@/storyblok/content/fare-table/ServiceBody.vue'

  type RowIndex = string | number

  const props = withDefaults(
    defineProps<{
      services: any[]
      visibleFares: any[]
      clubLogo?: AssetStoryblok
      clubLevel?: TFareTableClubLevel
      findFareService: (fare: any, service: any) => any
      hasExpandedContent: (service: ServiceSummaryStoryblok) => boolean
      isRowExpanded: (service: ServiceSummaryStoryblok, index: RowIndex) => boolean
      toggleRowExpanded: (service: ServiceSummaryStoryblok, index: RowIndex) => void
    }>(),
    {
      services: () => [],
      visibleFares: () => [],
      clubLogo: undefined,
      clubLevel: FARE_TABLE_CLUB_LEVEL.none
    }
  )

  const headerClass = cva('rounded-sm border border-border p-4 text-subtitle-1', {
    variants: {
      key: {
        ...fareHeaderColorVariants,
        default: ''
      }
    },
    defaultVariants: {
      key: 'default'
    }
  })

  const isServiceFull = (service: any) => service?.full === true || service?.full === 'true'
  const resolveFareService = (fare: any, service: any) => props.findFareService(fare, service)

  import { computed } from 'vue'

  const {
    hasExpandedContent,
    isRowExpanded,
    toggleRowExpanded,
    expandableRowIds,
    areAllRowsExpanded,
    setAllRowsExpanded
  } = useFareTableExpandableRows({
    services: computed(() => props.services),
    visibleFares: computed(() => props.visibleFares)
  })
</script>

<template>
  <div class="sticky top-19 z-20 bg-white px-4 py-2">
    <FareTableExpandAllSwitch
      :has-expandable-rows="expandableRowIds.length > 0"
      :model-value="areAllRowsExpanded"
      inline
      @update:model-value="setAllRowsExpanded"
    />
  </div>

  <div
    v-for="fare in visibleFares"
    :key="`desktop-${fare.key || fare._uid}`"
    :class="[headerClass({ key: fare.key }), 'sticky top-19 z-20']"
  >
    {{ fare.title }}
  </div>

  <template
    v-for="(service, index) in services"
    :key="`desktop-${service._uid || index}`"
  >
    <div class="rounded-sm border border-border bg-gradient-to-b from-white to-grey p-4">
      <FareTableServiceSummary
        :blok="service"
        :logo="clubLogo"
        :row-expanded="isRowExpanded(service, index)"
        :expandable="!isServiceFull(service) && hasExpandedContent(service)"
        @update:expanded="toggleRowExpanded(service, index)"
      />
    </div>
    <template v-if="!isServiceFull(service)">
      <div
        v-for="fare in visibleFares"
        :key="`service-desktop-${service._uid || index}-${fare.key || fare._uid}`"
        class="flex flex-col gap-2 rounded-sm border border-border bg-white p-4"
      >
        <FareTableServiceBody
          v-if="resolveFareService(fare, service)"
          :blok="resolveFareService(fare, service)"
          :logo="clubLogo"
          :club-level="clubLevel"
          :expanded="isRowExpanded(service, index)"
        />
      </div>
    </template>
    <div
      v-else
      class="rounded-sm border border-border bg-white p-4 md:col-[2/-1]"
    >
      <div v-html="marked(service.description)" />
    </div>
  </template>
</template>
